import type { PropsWithChildren, ReactNode } from 'react';
import { Skeleton, Typography } from 'antd';
import type { TooltipProps, BadgeProps } from 'antd';
import type { TextProps } from 'antd/es/typography/Text';

import { isElementWithDisplayName } from './utils';
import { SkyLabelWrapper } from './components/SkyLabelWrapper';
import { SkyLabelBadge } from './components/SkyLabelBadge';
import { SkyCopyable, COPYABLE_TEXT_PROPS } from '@skynet/sky-copyable';

const { Text } = Typography;

/**
 * Base props shared by all SkyLabel variants
 */
export interface SkyLabelProps extends PropsWithChildren {
  /**
   * Optional CSS class name to apply to the component
   */
  className?: string;

  /**
   * Configuration for copy-to-clipboard functionality
   * Can be a boolean or an object with copyable options
   */
  copyable?: TextProps['copyable'];

  /**
   * Icon element to display before the label text
   */
  icon?: ReactNode;

  /**
   * Whether to show a skeleton loader instead of the label
   * @default false
   */
  loading?: boolean;

  /**
   * Tooltip to display when hovering over the badge indicator
   * Can be a string for simple tooltip or TooltipProps object for advanced configuration
   */
  tooltip?: string | TooltipProps;

  /**
   * Configuration for text ellipsis behavior
   * Can be a boolean or an object with ellipsis options (without tooltip)
   */
  ellipsis?: TextProps['ellipsis'];

  /**
   * Badge configuration options
   * Allows customization of badge appearance (color, dot, offset, etc.)
   * Only applies when tooltip is provided
   */
  badgeProps?: BadgeProps;
}

/**
 * SkyLabel component displays a styled label with optional features including:
 * - Icon support
 * - Loading state with skeleton
 * - Text ellipsis with dedicated tooltip
 * - Copy to clipboard
 * - Tooltip with badge indicator
 * - Badge customization (color, dot, offset, etc.)
 *
 * The label is styled with font-semibold, uppercase, and tracking-wider by default.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <SkyLabel>Status</SkyLabel>
 *
 * // With icon
 * <SkyLabel icon={<UserIcon />}>User Name</SkyLabel>
 *
 * // With badge tooltip
 * <SkyLabel tooltip='Additional info'>Important Label</SkyLabel>
 *
 * // With custom badge
 * <SkyLabel tooltip='Warning' badgeProps={{ color: 'red', dot: true }}>
 *   Critical Status
 * </SkyLabel>
 *
 * // With ellipsis and ellipsis tooltip
 * <SkyLabel ellipsis ellipsisTooltip='Full text'>Long Label</SkyLabel>
 *
 * // With both tooltips
 * <SkyLabel
 *   ellipsis
 *   ellipsisTooltip='Full account name'
 *   tooltip='Account ID: 123456'
 * >
 *   BN_USDT_F_XRP
 * </SkyLabel>
 *
 * // With Highlighter (no ellipsis allowed)
 * <SkyLabel tooltip='Search result'>
 *   <Highlighter searchWords={['term']} textToHighlight='Search term' />
 * </SkyLabel>
 * ```
 */
export const SkyLabel = ({
  badgeProps = {},
  children,
  className = '',
  copyable = undefined,
  ellipsis = undefined,
  icon = undefined,
  loading = false,
  tooltip = undefined,
}: SkyLabelProps) => {
  const wrapperProps = { className, icon };

  if (loading) {
    return (
      <span data-testid="sky-label-skeleton">
        <Skeleton.Input size="small" active />
      </span>
    );
  }

  // Runtime check: detect if children contains Highlighter component
  const isHighlighterChild = isElementWithDisplayName(children, 'Highlighter');

  if (ellipsis && isHighlighterChild) {
    throw new Error(
      'SkyLabel: Cannot use ellipsis with Highlighter component. ' +
        'Ellipsis would truncate highlighted content. ' +
        'Please remove either ellipsis prop or use plain text instead of Highlighter.',
    );
  }

  // Calculate copy value
  const getCopyValue = (): string => {
    if (typeof children === 'string') return children;
    if (typeof children === 'number') return String(children);
    // For complex children, try to extract text content
    return '';
  };

  // Handle ellipsis case with optional badge tooltip wrapper
  if (ellipsis) {
    const textWithEllipsis = (
      <Text
        ellipsis={ellipsis}
        copyable={copyable}
        {...COPYABLE_TEXT_PROPS}
        // antd cannot use CSS ellipsis once `copyable` is combined with `ellipsis`
        // (it needs to reserve room for the copy button), so it truncates in JS
        // from the element's own offsetWidth. Left to size itself from its content
        // the element would settle on the *truncated* width and could never grow
        // back when the viewport widens — the name stayed clipped until a page
        // reload (SKY-3589). Sizing it from the container keeps the measurement in
        // sync with the available space on every resize.
        className={`${COPYABLE_TEXT_PROPS.className} w-full min-w-0`}
      >
        {children}
      </Text>
    );

    // If badge tooltip is provided, wrap in SkyLabelBadge
    return (
      <SkyLabelWrapper {...wrapperProps}>
        <SkyLabelBadge
          tooltip={tooltip}
          badgeProps={badgeProps}
          copyable={!!copyable}
          ellipsis={!!ellipsis}
        >
          {textWithEllipsis}
        </SkyLabelBadge>
      </SkyLabelWrapper>
    );
  }

  // Handle badge tooltip without ellipsis
  if (tooltip) {
    const componentWithTooltip = (
      <SkyLabelBadge tooltip={tooltip} badgeProps={badgeProps}>
        {children}
      </SkyLabelBadge>
    );

    // If it's copyable, wrap component to apply copyable
    if (copyable) {
      return (
        <SkyLabelWrapper {...wrapperProps}>
          <SkyCopyable copyable={copyable ?? false} text={getCopyValue()}>
            {componentWithTooltip}
          </SkyCopyable>
        </SkyLabelWrapper>
      );
    }

    return <SkyLabelWrapper {...wrapperProps}>{componentWithTooltip}</SkyLabelWrapper>;
  }

  // Handle copyable only case
  if (copyable) {
    return (
      <SkyLabelWrapper {...wrapperProps}>
        <SkyCopyable copyable={copyable ?? false} text={getCopyValue()}>
          <SkyLabelBadge tooltip={tooltip} badgeProps={badgeProps}>
            {children}
          </SkyLabelBadge>
        </SkyCopyable>
      </SkyLabelWrapper>
    );
  }

  // Finally just wrap the label without any extra features
  return (
    <SkyLabelWrapper {...wrapperProps}>
      <span>{children}</span>
    </SkyLabelWrapper>
  );
};
